/**
 * Vessel Type Requirements Framework
 * Based STRICTLY on regulatory requirements from:
 * - WBC3 (Workboat Code Edition 3) for seagoing vessels
 * - COP13 (Inland Waters Small Passenger Boat Code) for inland passenger vessels ≤12 pax
 * - MSN 1823 for inland passenger vessels >12 pax
 * - MGN 469 for inland non-passenger vessels
 */

import { parseCategory } from '../utils/categoryHelpers';

// Base regulatory requirements that apply to ALL vessels based on operating area
// MINIMAL - only what's actually mandated by regulations
const baseRequirements = {
  seagoing: {
    // WBC3 doesn't mandate specific daily checks - these would be company SMS requirements
    preDeparture: [],
    dailyUnderway: [],
    alongside: [],
    daily: [] // No regulatory daily checks for seagoing vessels
  },
  inland: {
    // COP13 mentions "testing of equipment, including steering gear, prior to commencing a passage"
    // This is pre-departure, not daily checks
    preDeparture: [],
    dailyUnderway: [],
    alongside: [],
    daily: [] // Base daily checks - will be added for passenger vessels specifically
  }
};

// Suggested best practice checks (not regulatory requirements)
export const suggestedDailyChecks = {
  seagoing: [
    'Engine room visual inspection',
    'Bilges checked and pumped if needed',
    'Navigation lights tested',
    'Steering gear tested',
    'VHF radio check'
  ],
  inland: [
    'Steering gear operation',
    'Navigation equipment functional',
    'Safety equipment present and secure',
    'Mooring lines condition',
    'Engine checks (oil, coolant, belts)'
  ],
  passenger: [
    'Emergency exits clear',
    'Life jackets accessible',
    'PA system tested',
    'Passenger areas clean and safe'
  ]
};

// Vessel-specific additional requirements
export const vesselTypes = {
  // === PASSENGER VESSELS ===
  'pax': {
    label: 'Passenger Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: [],
      daily: [] // WBC3 doesn't mandate daily checks for seagoing passenger vessels
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: [],
      // COP13 Section 5.1 & MSN 1823 Section 4.1 - Daily checks for inland passenger vessels
      daily: [
        'Steering gear - test operation (COP13 5.1)',
        'Navigation equipment - verify functional (COP13 5.1)',
        'Emergency equipment - check present and secure (COP13 5.1)',
        'Communication equipment - test operation (COP13 5.1)',
        'Bilge pumps - test if fitted (COP13 5.1)',
        'Fire pumps - test if fitted (MSN 1823 4.1)',
        'Emergency exits - verify clear (COP13/MSN 1823)',
        'Life-saving appliances - check condition (COP13/MSN 1823)'
      ]
    },
    monthlyChecks: [
      'Fire/Abandon ship drill (SOLAS/MSN)',
      'Emergency equipment inspection'
    ],
    requiredLogs: ['Passenger counts', 'Safety briefings', 'Drills'],
    specialForms: ['passenger-manifest']
  },
  
  // === PILOT BOATS ===
  'pilot': {
    label: 'Pilot Boat',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Retrieval equipment - verify ready (WBC3 27.2)',
        'Searchlight - test operation (WBC3 14.12)',
        'Immersion suits - check all present (WBC3 14.5)'
      ],
      dailyUnderway: [
        ...baseRequirements.seagoing.dailyUnderway
      ],
      alongside: []
    },
    inland: {
      // Pilot boats on inland waters follow base inland requirements
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [
      'Retrieval equipment - test operation (WBC3 27.2)',
      'Man ropes - inspect if carried (WBC3 27.2)'
    ],
    requiredLogs: ['Pilot Transfer Log', 'Transfer conditions'],
    specialForms: ['pilot-transfer']
  },
  
  // === TUG/TOWING VESSELS ===
  'tug': {
    label: 'Tug/Towing Vessel',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Towing equipment - visual inspection (WBC3 26)',
        'Emergency release - verify accessible (WBC3 26)'
      ],
      dailyUnderway: [
        ...baseRequirements.seagoing.dailyUnderway,
        'Towing configuration lights - check if towing (WBC3)'
      ],
      alongside: []
    },
    inland: {
      preDeparture: [
        ...baseRequirements.inland.preDeparture,
        'Towing equipment - inspect (MSN 1853)'
      ],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [
      'Towing winch - operation test (WBC3)',
      'Emergency release - test (WBC3)'
    ],
    requiredLogs: ['Towing operations', 'Tow configuration'],
    specialForms: ['towing-log']
  },
  
  // === MULTIPURPOSE WORKBOAT ===
  'multipurpose': {
    label: 'Multipurpose Workboat',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Departure/Arrival', 'Incidents'],
    specialForms: []
  },
  
  // === RIB ===
  'rib': {
    label: 'Rigid Inflatable Boat',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Tube pressure - check (manufacturer specs)',
        'Kill cord - test if >17 knots (WBC3 25.4)'
      ],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [
        ...baseRequirements.inland.preDeparture,
        'Tube pressure - check (manufacturer specs)'
      ],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Departure/Arrival'],
    specialForms: []
  },
  
  // === SURVEY VESSEL ===
  'survey': {
    label: 'Survey Vessel',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Survey equipment - verify operational status'
      ],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Survey operations', 'Equipment calibration'],
    specialForms: []
  },
  
  // === DIVE SUPPORT ===
  'dive_support': {
    label: 'Dive Support Vessel',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Diving ladder - check before each use (WBC3 25.5)'
      ],
      dailyUnderway: [
        ...baseRequirements.seagoing.dailyUnderway,
        'Emergency gas - check pressure when diving (WBC3 25.5)'
      ],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [
      'Diver recall system - test (WBC3 25.5)'
    ],
    requiredLogs: ['Diving operations', 'Diver hours'],
    specialForms: ['dive-log']
  },
  
  // === POLICE/PATROL VESSELS ===
  'police': {
    label: 'Police/Patrol Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Patrol activities', 'Incidents'],
    specialForms: ['incident-report']
  },
  
  'patrol': {
    label: 'Patrol Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Patrol activities'],
    specialForms: []
  },
  
  // === CTV ===
  'ctv': {
    label: 'Crew Transfer Vessel',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Transfer system - check operational (WBC3)'
      ],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Personnel transfers', 'Weather conditions'],
    specialForms: ['crew-transfer']
  },
  
  // === CARGO ===
  'cargo': {
    label: 'Cargo Vessel',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'Cargo securing - verify (CSS Code if applicable)'
      ],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Cargo operations'],
    specialForms: []
  },
  
  // === UTILITY ===
  'utility': {
    label: 'Utility Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Work activities'],
    specialForms: []
  },
  
  // === SUPPORT VESSELS ===
  'support': {
    label: 'Support Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Support operations'],
    specialForms: []
  },
  
  'fishing_support': {
    label: 'Fishing Support Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Fishing operations'],
    specialForms: []
  },
  
  // === INLAND SPECIFIC VESSELS ===
  'hire_self_drive': {
    label: 'Self-Drive Hire Boat',
    seagoing: null, // Not applicable for seagoing
    inland: {
      preDeparture: [
        'Safety equipment - check present (AINA Code)',
        'Engine fuel/oil - check levels (AINA Code)',
        'Handover briefing - complete (AINA Code)'
      ],
      dailyUnderway: [],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Hire periods', 'Handover checks'],
    specialForms: ['hire-agreement']
  },
  
  'hire_skippered': {
    label: 'Skippered Hire Boat',
    seagoing: null,
    inland: {
      preDeparture: [
        ...baseRequirements.inland.preDeparture
      ],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: [],
      // If carrying passengers for hire, daily checks apply per COP13
      daily: [
        'Steering gear - test operation (COP13 5.1)',
        'Navigation equipment - verify functional (COP13 5.1)',
        'Emergency equipment - check present and secure (COP13 5.1)',
        'Communication equipment - test operation (COP13 5.1)',
        'Bilge pumps - test if fitted (COP13 5.1)',
        'Emergency exits - verify clear (COP13)',
        'Life-saving appliances - check condition (COP13)'
      ]
    },
    monthlyChecks: [],
    requiredLogs: ['Hire periods', 'Passenger briefings'],
    specialForms: []
  },
  
  'trip_boat': {
    label: 'Trip Boat',
    seagoing: null,
    inland: {
      preDeparture: [
        ...baseRequirements.inland.preDeparture,
        'Emergency exits - verify clear (MSN 1823)'
        // Note: Passenger count and safety announcements are voyage-specific
      ],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: [],
      // COP13 Section 5.1 & MSN 1823 Section 4.1 - Daily checks for inland passenger vessels
      daily: [
        'Steering gear - test operation (COP13 5.1)',
        'Navigation equipment - verify functional (COP13 5.1)',
        'Emergency equipment - check present and secure (COP13 5.1)',
        'Communication equipment - test operation (COP13 5.1)',
        'Bilge pumps - test if fitted (COP13 5.1)',
        'Fire pumps - test if fitted (MSN 1823 4.1)',
        'Emergency exits - verify clear (COP13/MSN 1823)',
        'Life-saving appliances - check condition (COP13/MSN 1823)'
      ]
    },
    monthlyChecks: [],
    requiredLogs: ['Passenger counts', 'Trip times'],
    specialForms: ['passenger-manifest']
  },
  
  'narrowboat_commercial': {
    label: 'Commercial Narrowboat',
    seagoing: null,
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Lock passages', 'Commercial operations'],
    specialForms: []
  },
  
  // Additional common vessel types
  'hydrographic': {
    label: 'Hydrographic Vessel',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Survey operations'],
    specialForms: []
  },
  
  'dredger': {
    label: 'Dredger',
    seagoing: {
      preDeparture: [...baseRequirements.seagoing.preDeparture],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Dredging operations', 'Disposal records'],
    specialForms: []
  },
  
  'safety_standby': {
    label: 'Safety/Standby Boat',
    seagoing: {
      preDeparture: [
        ...baseRequirements.seagoing.preDeparture,
        'MOB recovery equipment - check ready (WBC3)'
      ],
      dailyUnderway: [...baseRequirements.seagoing.dailyUnderway],
      alongside: []
    },
    inland: {
      preDeparture: [...baseRequirements.inland.preDeparture],
      dailyUnderway: [...baseRequirements.inland.dailyUnderway],
      alongside: []
    },
    monthlyChecks: [],
    requiredLogs: ['Safety standby hours', 'Incidents'],
    specialForms: []
  }
};

/**
 * Get required checks based on vessel type, operational status, operating area, and vessel specifics
 */
export const getRequiredChecks = (vesselType, operationalStatus, operatingArea = 'seagoing', vesselConfig = {}) => {
  const vessel = vesselTypes[vesselType];
  if (!vessel) return { daily: [], monthly: [], quarterly: [], annual: [] };
  
  // Extract vessel configuration
  const {
    grossTonnage = 0,
    lengthOverall = 0,
    maxPassengers = 0,
    category = '6',  // Water category (0-6)
    hasPassengers = false,
    carryingPassengersToday = false,  // New flag for actual passenger carriage
    internationalVoyages = false,
    operatingHours = 'under_24h'
  } = vesselConfig;
  
  // Parse values - use parseCategory for category to handle 'cat3', 3, '3', 'CAT3' formats
  const gt = parseInt(grossTonnage) || 0;
  const length = parseInt(lengthOverall) || 0;
  const passengers = parseInt(maxPassengers) || 0;
  const waterCat = parseCategory(category);
  
  // Determine if vessel is seagoing or inland
  const isInland = operatingArea === 'inland';
  const requirements = isInland ? vessel.inland : vessel.seagoing;
  
  // If vessel type doesn't support this operating area, return empty
  if (!requirements) return { daily: [], monthly: [], quarterly: [], annual: [] };
  
  // Map operational status to check requirements
  let dailyChecks = [];
  let additionalChecks = [];
  
  // Get regulatory daily checks if they exist for this vessel type/operating area
  if (requirements.daily && requirements.daily.length > 0) {
    dailyChecks = [...requirements.daily];
  }
  
  // Pre-departure and underway checks are separate from daily checks
  let preDepartureChecks = [];
  if (operationalStatus === 'preDeparture' || operationalStatus === 'underway') {
    // Pre-departure checks should be shown before sailing
    preDepartureChecks = requirements.preDeparture || [];
    
    // Add underway checks if actually underway
    if (operationalStatus === 'underway') {
      preDepartureChecks = [...preDepartureChecks, ...(requirements.dailyUnderway || [])];
    }
  } else if (operationalStatus === 'alongside') {
    preDepartureChecks = requirements.alongside || [];
  }
  
  // === CONDITIONAL REQUIREMENTS BASED ON VESSEL SPECIFICS ===
  
  // Water Category specific (seagoing only)
  if (!isInland) {
    if (waterCat <= 2) {
      // Offshore operations (Cat 0, 1, 2)
      additionalChecks.push('EPIRB - test operation (WBC3 Cat 0-2)');
      additionalChecks.push('SART - test if fitted (WBC3 Cat 0-2)');
    }
    if (waterCat <= 4 && operationalStatus === 'preDeparture') {
      // Coastal and offshore
      additionalChecks.push('Weather forecast - obtain and assess (WBC3)');
    }
  }
  
  // Tonnage-based requirements
  if (gt >= 300 && !isInland) {
    // SOLAS applicability threshold for certain requirements
    additionalChecks.push('GMDSS radio - test DSC (SOLAS)');
  }
  
  // Length-based requirements
  if (length >= 24) {
    // Larger vessels
    if (operationalStatus === 'preDeparture') {
      additionalChecks.push('Bridge equipment - test all (>24m requirement)');
    }
  }
  
  // Note: Passenger-specific requirements like passenger count, manifest, and safety briefings
  // are handled in the voyage pre-departure workflow, not daily checks
  
  // Operating hours requirements
  if (operatingHours === '24h_plus' || operatingHours === 'over_24h') {
    if (operationalStatus === 'preDeparture') {
      additionalChecks.push('Night navigation equipment - test (>24h ops)');
      additionalChecks.push('Accommodation areas - check secure (>24h ops)');
    }
  }
  
  // International voyages
  if (internationalVoyages && !isInland) {
    if (operationalStatus === 'preDeparture') {
      additionalChecks.push('Ship security - verify ISPS compliance (International)');
      additionalChecks.push('Port clearance - verify obtained (International)');
    }
  }
  
  return {
    daily: dailyChecks,  // Regulatory daily checks (separate from pre-departure)
    preDeparture: [...preDepartureChecks, ...additionalChecks],  // Pre-departure checks
    monthly: vessel.monthlyChecks || [],
    quarterly: [],  // No quarterly checks specified in regulations
    annual: []  // Annual checks are maintenance/survey items, not daily operational
  };
};

/**
 * Get regulatory framework based on operating area
 */
export const getRegulatoryFramework = (vesselType, operatingArea = 'seagoing') => {
  const vessel = vesselTypes[vesselType];
  
  if (operatingArea === 'inland') {
    // Inland waterways regulations
    if (vesselType === 'pax' || vesselType === 'trip_boat') {
      return { 
        code: 'COP13/MSN1823', 
        description: 'Inland Waters Passenger Codes' 
      };
    }
    if (vesselType.includes('hire')) {
      return { 
        code: 'AINA 2021', 
        description: 'Hire Boat Code' 
      };
    }
    return { 
      code: 'MGN 469', 
      description: 'Inland Waterways Non-Passenger Vessels' 
    };
  }
  
  // Seagoing vessels
  return { 
    code: 'WBC3', 
    description: 'Workboat Code Edition 3' 
  };
};

/**
 * Get required log entries based on vessel configuration
 */
export const getRequiredLogEntries = (vesselType) => {
  const vessel = vesselTypes[vesselType];
  return vessel?.requiredLogs || ['Departure/Arrival', 'Incidents'];
};

/**
 * Get special forms required for vessel type
 */
export const getSpecialForms = (vesselType) => {
  const vessel = vesselTypes[vesselType];
  return vessel?.specialForms || [];
};

export default {
  vesselTypes,
  getRequiredChecks,
  getRegulatoryFramework,
  getRequiredLogEntries,
  getSpecialForms
};